/*
 * iqoser · Afterglow v2 — shared engine for every page.
 *
 * Pages mark up what they need; this file wires it:
 *   [data-swatches]            finish picker (adds a 3 s progress ring when the hero is cycling)
 *   [data-finish-*]            text/img bound to the selected finish
 *   [data-add-device]          adds the selected finish (qty from [data-qty] if present)
 *   [data-flavours]            TEREA cards from the <template> inside, optional data-flavours="Family"
 *   [data-hero]                hero stage: data-motion="float|orbit", data-glow="halo|disc|beam"
 *   [data-howto]               scroll-scrubbed how-to with the real light order and the stick going in
 *   [data-buybar]              sticky buy bar once [data-buybar-after] has scrolled away
 *   [data-vary]                mockup-only panel to switch hero variations
 *
 * Product images are official PMI renders. The status lights are drawn over the real slot,
 * in the verified order: fill bottom→top while heating, switch off top→bottom while in use.
 */
(() => {
  const A = window.IQ_ASSETS || 'assets/';
  const inr = (n) => '₹' + n.toLocaleString('en-IN');
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  // Prices and variant ids come from Shopify (window.IQ_DATA, rendered by snippets/iq-data.liquid).
  const FINISHES = [
    { id: 'breeze-blue', name: 'Breeze Blue', hex: '#AED1D4', deep: '#2E4A4D', up: 0, views: [0, 1, 2, 3, 4], price: 4990 },
    { id: 'leaf-green', name: 'Leaf Green', hex: '#B9C66A', deep: '#3D4519', up: 0, views: [0, 1, 2, 3, 4], price: 4990 },
    { id: 'digital-violet', name: 'Digital Violet', hex: '#A9B4EC', deep: '#2E3566', up: 0, views: [0, 1, 2, 3, 4], price: 4990 },
    { id: 'midnight-black', name: 'Midnight Black', hex: '#7D8496', deep: '#1E2129', up: 0, views: [0, 1, 2, 3, 4], price: 4990 },
    { id: 'electric-purple', name: 'Electric Purple', hex: '#5B46E0', deep: '#1D1352', up: 3, views: [3, 0, 2, 5, 1, 4], price: 5290 },
  ];
  const FLAVOURS = [
    { id: 'amber', name: 'Amber', family: 'Tobacco', note: 'Balanced, roasted tobacco. The everyday one.', hex: '#D9862F', pack: 320, carton: 3000 },
    { id: 'bronze', name: 'Bronze', family: 'Tobacco', note: 'Rich and full, with cocoa and dried-fruit notes.', hex: '#8A5232', pack: 360, carton: 3400 },
    { id: 'silver', name: 'Silver', family: 'Tobacco', note: 'Refined, lighter toasted tobacco.', hex: '#B7B2A8', pack: 320, carton: 3000 },
    { id: 'turquoise', name: 'Turquoise', family: 'Menthol', note: 'Cool menthol over toasted tobacco, with a zesty edge.', hex: '#1FA3A0', pack: 360, carton: 3400 },
    { id: 'purple-wave', name: 'Purple Wave', family: 'Menthol', note: 'Menthol tobacco with dark forest-fruit notes.', hex: '#6E4BD8', pack: 320, carton: 3000 },
    { id: 'sun-pearl', name: 'Sun Pearl', family: 'Capsule', note: 'Deep menthol tobacco. Click the capsule for cool watermelon.', hex: '#E5674B', pack: 360, carton: 3400 },
    { id: 'oasis-pearl', name: 'Oasis Pearl', family: 'Capsule', note: 'Toasted, creamy tobacco. Click for passion fruit and menthol.', hex: '#B04A86', pack: 320, carton: 3000 },
  ];

  const D = window.IQ_DATA || { device: {}, terea: {} };
  FINISHES.forEach((f) => { const v = D.device[f.id]; if (v) { f.price = v.price; f.vid = v.id; f.available = v.available; } });
  FLAVOURS.forEach((fl) => { const t = D.terea[fl.id]; if (t) ['pack', 'carton'].forEach((u) => { if (t[u]) { fl[u] = t[u].price; fl[u + 'Id'] = t[u].id; } }); });

  // Status-light geometry, as fractions of each render's box. Measured on the official renders.
  const UPRIGHT = {
    default: { x: 0.9057, w: 0.022, rows: [[0.1326, 0.1547], [0.1581, 0.1768], [0.1802, 0.1989], [0.2023, 0.2203]] },
    'electric-purple': { x: 0.925, w: 0.0156, rows: [[0.1229, 0.145], [0.1492, 0.1678], [0.172, 0.1906], [0.1948, 0.2134]] },
  };
  // Angled, cap-open render (717×1448): bar centres top→bottom, tilted 21.6° with the body.
  const ANGLED = { tilt: 21.58, len: 0.0262, thick: 0.0196, c: [[0.1696, 0.2692], [0.1874, 0.2917], [0.2041, 0.3124], [0.22, 0.3327]], hole: [0.2515, 0.0475], stick: 0.104 };

  const img = (f, v) => `${A}${f.id}-${v}.webp`;
  const upright = (f, hd) => (hd ? `${A}${f.id}-upright-hd.webp` : img(f, f.up));

  const placeUpright = (bars, f) => {
    const g = UPRIGHT[f.id] || UPRIGHT.default;
    bars.forEach((b, i) => {
      const [y0, y1] = g.rows[i], px = g.w * 0.25, py = 0.0016;
      Object.assign(b.style, { left: (g.x - px) * 100 + '%', width: (g.w + 2 * px) * 100 + '%', top: (y0 - py) * 100 + '%', height: (y1 - y0 + 2 * py) * 100 + '%' });
    });
  };
  const placeAngled = (bars) => {
    bars.forEach((b, i) => {
      const [cx, cy] = ANGLED.c[i];
      Object.assign(b.style, { left: (cx - ANGLED.thick / 2) * 100 + '%', top: (cy - ANGLED.len / 2) * 100 + '%', width: ANGLED.thick * 100 + '%', height: ANGLED.len * 100 + '%', transform: `rotate(${-ANGLED.tilt}deg)` });
    });
  };
  // state: {on:[top..bottom 0/1]} or {fill:n} or {level:n}, pulse index from bottom (-1 none)
  const setLights = (bars, s) => {
    bars.forEach((b, i) => {
      const fromBottom = 3 - i;
      let on = 0, pulse = false;
      if (s.all !== undefined) on = s.all;
      else if (s.fill !== undefined) { on = fromBottom < Math.floor(s.fill) ? 1 : 0; pulse = s.fill < 4 && fromBottom === Math.floor(s.fill); }
      else if (s.level !== undefined) { on = fromBottom < s.level ? 1 : 0; pulse = s.pulseBottom && fromBottom === 0; }
      b.classList.toggle('is-pulse', pulse);
      b.style.setProperty('--on', pulse ? 1 : on);
    });
  };

  /* ---------- finish state ---------- */
  let current = FINISHES[0];
  const subs = [];
  const setFinish = (id, why) => {
    const f = FINISHES.find((x) => x.id === id);
    if (!f) return;
    current = f;
    const root = document.documentElement.style;
    root.setProperty('--finish', f.hex);
    root.setProperty('--finish-deep', f.deep);
    document.documentElement.dataset.finish = f.id;
    $$('[data-finish-name]').forEach((el) => (el.textContent = f.name));
    $$('[data-finish-price]').forEach((el) => (el.textContent = inr(f.price)));
    $$('img[data-finish-img]').forEach((el) => (el.src = el.dataset.finishImg === 'upright' ? upright(f) : el.dataset.finishImg === 'upright-hd' ? upright(f, true) : img(f, el.dataset.finishImg)));
    $$('[data-swatch]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.swatch === f.id)));
    subs.forEach((fn) => fn(f, why));
  };

  const RING = '<svg viewBox="0 0 44 44" aria-hidden="true"><circle cx="22" cy="22" r="21"/></svg>';
  const buildSwatches = () => $$('[data-swatches]').forEach((wrap) => {
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Finish');
    FINISHES.forEach((f) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.dataset.swatch = f.id;
      b.style.setProperty('--sw', f.hex);
      b.setAttribute('aria-label', f.name);
      b.title = f.name;
      b.innerHTML = '<span class="sw-dot"></span>' + RING;
      b.addEventListener('click', () => setFinish(f.id, 'user'));
      wrap.appendChild(b);
    });
  });

  /* ---------- bag = the Shopify cart (AJAX API), same interface as the mockup's bag ---------- */
  const VMAP = {};
  const post = (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }).then((r) => r.json());
  const Bag = {
    items: [],
    _q: Promise.resolve(),
    qty(key) { return (this.items.find((i) => i.key === key) || {}).qty || 0; },
    queue(fn) { this._q = this._q.then(fn).catch(() => this.sync()); return this._q; },
    sync(cart) {
      return (cart ? Promise.resolve(cart) : fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json())).then((c) => {
        this.items = (c.items || []).map((li) => {
          const mk = VMAP[li.variant_id];
          const base = mk ? mk() : { key: 'v' + li.variant_id, title: li.product_title, sub: li.variant_title || '', img: li.image };
          return { ...base, qty: li.quantity, price: li.final_price / 100, line: li.key, vid: li.variant_id };
        });
        this.render();
      });
    },
    add(item, n = 1, quiet) {
      if (!item.vid) return Promise.resolve();
      const found = this.items.find((i) => i.key === item.key);
      if (found) found.qty += n; else this.items.push({ ...item, qty: n });
      this.render();
      if (!quiet) toast(item, n);
      $$('.bag-btn').forEach((b) => { b.classList.remove('bump'); void b.offsetWidth; b.classList.add('bump'); });
      return this.queue(() => post('/cart/add.js', { items: [{ id: item.vid, quantity: n }] }).then((r) => { if (r && r.status) throw r; return this.sync(); }));
    },
    change(key, d) {
      const it = this.items.find((i) => i.key === key);
      if (!it) return Promise.resolve();
      it.qty += d;
      const q = Math.max(0, it.qty);
      if (it.qty <= 0) this.items = this.items.filter((i) => i !== it);
      this.render();
      return this.queue(() => post('/cart/change.js', { id: it.line || String(it.vid), quantity: q }).then((c) => this.sync(c && c.items ? c : null)));
    },
    open() {
      const el = $('[data-bag]');
      el.hidden = false;
      requestAnimationFrame(() => el.classList.add('is-open'));
      $('[data-bag-close]', el).focus();
    },
    close() {
      const el = $('[data-bag]');
      if (!el) return;
      el.classList.remove('is-open');
      setTimeout(() => (el.hidden = true), 320);
    },
    render() {
      const count = this.items.reduce((s, i) => s + i.qty, 0);
      $$('[data-bag-count]').forEach((el) => (el.textContent = count));
      const list = $('[data-bag-list]');
      if (list) {
        list.innerHTML = this.items.length ? this.items.map((i) => `
          <div class="bag-item">
            <img src="${i.img}" alt="">
            <div class="bag-item-text"><strong>${i.title}</strong><span>${i.sub}</span></div>
            <div class="bag-qty"><button type="button" aria-label="One fewer" data-q="${i.key}" data-d="-1">−</button><span>${i.qty}</span><button type="button" aria-label="One more" data-q="${i.key}" data-d="1">+</button></div>
            <span class="bag-price">${inr(i.price * i.qty)}</span>
          </div>`).join('') : '<p class="bag-empty">Your bag is empty. Pick a finish, or a few packs of TEREA.</p>';
        $$('[data-q]', list).forEach((b) => b.addEventListener('click', () => this.change(b.dataset.q, +b.dataset.d)));
      }
      const up = $('[data-upsell]');
      if (up) {
        const hasDevice = this.items.some((i) => i.key.startsWith('device-'));
        const hasSticks = this.items.some((i) => i.key.startsWith('terea-'));
        up.hidden = !(hasDevice && !hasSticks);
      }
      $$('[data-bag-total]').forEach((el) => (el.textContent = inr(this.items.reduce((s, i) => s + i.qty * i.price, 0))));
      $$('[data-checkout]').forEach((el) => (el.disabled = !this.items.length));
      $$('[data-stepper-for]').forEach((s) => s.dispatchEvent(new Event('sync')));
    },
  };
  const deviceItem = (f) => ({ key: `device-${f.id}`, title: 'IQOS ILUMA i ONE', sub: f.name, price: f.price, img: upright(f), vid: f.vid });
  const stickItem = (fl, unit) => ({ key: `terea-${fl.id}-${unit}`, title: `TEREA ${fl.name}`, sub: unit === 'carton' ? 'Carton · 10 packs' : 'Pack · 20 sticks', price: fl[unit], img: `${A}terea-${fl.id}.webp`, vid: fl[unit + 'Id'] });
  FINISHES.forEach((f) => f.vid && (VMAP[f.vid] = () => deviceItem(f)));
  FLAVOURS.forEach((fl) => ['pack', 'carton'].forEach((u) => fl[u + 'Id'] && (VMAP[fl[u + 'Id']] = () => stickItem(fl, u))));

  let toastTimer;
  const toast = (item, n) => {
    const t = $('[data-toast]');
    if (!t) return;
    $('img', t).src = item.img;
    $('p', t).innerHTML = `Added ${n > 1 ? n + ' × ' : ''}${item.title}<span>${item.sub}</span>`;
    t.classList.add('show');
    clearTimeout(toastTimer);
    const hide = () => (toastTimer = setTimeout(() => t.classList.remove('show'), 4500));
    t.onmouseenter = () => clearTimeout(toastTimer);
    t.onmouseleave = hide;
    hide();
  };

  const injectChrome = () => {
    document.body.insertAdjacentHTML('beforeend', `
      <div class="bag" data-bag hidden>
        <aside class="bag-panel" aria-label="Bag">
          <div class="bag-top"><h2>Your bag</h2><button type="button" data-bag-close>Close</button></div>
          <div class="bag-list" data-bag-list></div>
          <div class="bag-upsell" data-upsell hidden>
            <p>Your device needs TEREA sticks. Add a pack:</p>
            <div class="row">${FLAVOURS.map((f) => `<button type="button" data-upsell-add="${f.id}"><img src="${A}terea-${f.id}.webp" alt="">${f.name}</button>`).join('')}</div>
          </div>
          <div class="bag-foot">
            <div class="row2"><span>Total</span><strong data-bag-total>₹0</strong></div>
            <button class="cta" type="button" data-checkout onclick="location.href='/checkout'">Check out</button>
            <small>Delivery options for your pincode are shown at checkout.</small>
          </div>
        </aside>
      </div>
      <div class="toast" data-toast role="status" aria-live="polite"><img alt=""><p></p><button type="button" data-bag-open>View bag</button></div>
      <div class="gate" data-gate hidden>
        <div class="gate-card">
          <span class="mark"><span class="bars" aria-hidden="true"><i></i><i></i><i></i><i></i></span>iqoser</span>
          <p>This site sells tobacco products. It is only for adults who smoke or use nicotine.</p>
          <div class="gate-actions"><button class="cta" type="button" data-gate-yes>I'm 18 or older</button><button class="ghost" type="button" data-gate-no>I'm under 18</button></div>
          <p data-gate-msg hidden>Sorry, you can't use this site.</p>
        </div>
      </div>`);
    $$('[data-upsell-add]').forEach((b) => b.addEventListener('click', () => { const fl = FLAVOURS.find((x) => x.id === b.dataset.upsellAdd); Bag.add(stickItem(fl, 'pack'), 1, true); }));
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-bag-open]')) { $('[data-toast]').classList.remove('show'); Bag.open(); }
      if (e.target.closest('[data-bag-close]') || e.target.matches('[data-bag]')) Bag.close();
    });
    addEventListener('keydown', (e) => { if (e.key === 'Escape') Bag.close(); });
    $$('[data-add-device]').forEach((b) => b.addEventListener('click', () => {
      const q = $('[data-qty]');
      Bag.add(deviceItem(current), q ? +q.value || 1 : 1);
    }));
    const top = $('.top');
    if (top) addEventListener('scroll', () => top.classList.toggle('scrolled', scrollY > 8), { passive: true });

    // age check
    const gate = $('[data-gate]');
    let ok = false;
    try { ok = sessionStorage.getItem('iq-age') === '1' || location.search.includes('nogate'); } catch (e) {}
    if (ok) gate.remove();
    else {
      gate.hidden = false;
      document.documentElement.classList.add('gated');
      $('[data-gate-yes]', gate).addEventListener('click', () => {
        try { sessionStorage.setItem('iq-age', '1'); } catch (e) {}
        gate.classList.add('is-leaving');
        document.documentElement.classList.remove('gated');
        setTimeout(() => gate.remove(), 500);
      });
      $('[data-gate-no]', gate).addEventListener('click', () => ($('[data-gate-msg]', gate).hidden = false));
    }
  };

  /* ---------- TEREA cards: Pack | Carton toggle (equal halves) + one add button ---------- */
  const quickAdd = (host, fl) => {
    let unit = host.dataset.unit || 'pack';
    const keys = { pack: `terea-${fl.id}-pack`, carton: `terea-${fl.id}-carton` };
    const draw = () => {
      host.innerHTML = `<div class="seg" role="radiogroup" aria-label="${fl.name} size">${['pack', 'carton'].map((u) => {
        const q = Bag.qty(keys[u]);
        return `<button type="button" role="radio" aria-checked="${u === unit}" data-u="${u}" title="${u === 'pack' ? '20 sticks' : '10 packs, 200 sticks'}"><span>${u === 'pack' ? 'Pack' : 'Carton'}</span><b>${inr(fl[u])}</b>${q ? `<i class="cnt" aria-label="${q} in bag">${q}</i>` : ''}</button>`;
      }).join('')}</div><button type="button" class="add" aria-label="Add a ${unit} of ${fl.name} to bag">+</button>`;
      $$('[data-u]', host).forEach((b) => b.addEventListener('click', () => { unit = b.dataset.u; draw(); }));
      $('.add', host).addEventListener('click', (e) => {
        Bag.add(stickItem(fl, unit));
        const a = $('.add', host);
        if (a) { a.classList.add('done'); a.textContent = '✓'; clearTimeout(host._t); host._t = setTimeout(draw, 900); }
      });
    };
    host.dataset.stepperFor = keys.pack;
    host.addEventListener('sync', () => { if (!host.querySelector('.add.done')) draw(); });
    draw();
  };
  const buildFlavours = () => $$('[data-flavours]').forEach((wrap) => {
    const tpl = $('template', wrap);
    const fam = wrap.dataset.flavours;
    const skip = wrap.dataset.skip;
    const pick = wrap.dataset.pick ? wrap.dataset.pick.split(',') : null;
    FLAVOURS.filter((f) => (!fam || f.family === fam) && f.id !== skip && (!pick || pick.includes(f.id))).forEach((fl) => {
      const node = tpl.content.firstElementChild.cloneNode(true);
      node.dataset.family = fl.family;
      node.style.setProperty('--pack', fl.hex);
      $$('[data-fl]', node).forEach((el) => {
        const k = el.dataset.fl;
        if (k === 'img') { el.src = `${A}terea-${fl.id}.webp`; el.alt = `TEREA ${fl.name} pack`; }
        else if (k === 'href') el.href = `/products/terea-${fl.id}`;
        else el.textContent = fl[k];
      });
      $$('[data-qa]', node).forEach((h) => quickAdd(h, fl));
      wrap.appendChild(node);
    });
  });

  /* ---------- hero ---------- */
  const wireHero = () => {
    const hero = $('[data-hero]');
    if (!hero) return;
    const params = new URLSearchParams(location.search);
    hero.dataset.motion = params.get('motion') || hero.dataset.motion || 'orbit';
    hero.dataset.glow = params.get('glow') || hero.dataset.glow || 'halo';
    const sw = $('[data-swatches]', hero);
    const CYCLE = 3000;
    sw && sw.style.setProperty('--cycle', CYCLE + 'ms');

    // float: two stacked renders crossfade; the lights re-run their heat-up fill each time
    const shot = $('[data-hero-shot]', hero);
    const [imA, imB] = $$('img', shot);
    const bars = $$('.light', shot);
    let front = imA;
    const fillLights = () => {
      if (reduce.matches) { setLights(bars, { all: 1 }); return; }
      let n = 0;
      setLights(bars, { fill: 0 });
      clearInterval(hero._fill);
      hero._fill = setInterval(() => { n += 1; setLights(bars, { fill: n }); if (n >= 4) clearInterval(hero._fill); }, 260);
    };
    const showFloat = (f) => {
      const back = front === imA ? imB : imA;
      const token = (hero._token = (hero._token || 0) + 1);
      back.onload = null;
      back.src = upright(f, true);
      const swap = () => {
        if (token !== hero._token) return; // a newer change has started
        placeUpright(bars, f);
        back.classList.add('on');
        if (front !== back) front.classList.remove('on');
        front = back;
        fillLights();
      };
      if (back.complete && back.naturalWidth) swap();
      else back.onload = () => { back.onload = null; swap(); };
    };

    // orbit: the five finishes on a ring. The one turning to the front grows to full size,
    // its lights power up bottom to top, it hovers, then the ring turns to the next.
    const orbit = $('[data-orbit]', hero);
    const items = FINISHES.map((f) => {
      const el = document.createElement('div');
      el.className = 'orb';
      el.innerHTML = `<div class="orb-in"><img src="${upright(f, true)}" alt=""><i class="light"></i><i class="light"></i><i class="light"></i><i class="light"></i></div>`;
      const bars = $$('.light', el);
      placeUpright(bars, f);
      setLights(bars, { all: 0 });
      el.addEventListener('click', () => setFinish(f.id, 'user'));
      orbit.appendChild(el);
      return { el, bars };
    });
    const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2);
    let angle = 0, from = 0, target = 0, t0 = 0, dur = 900, settled = true, focusIdx = -1;
    const R = () => Math.min(innerWidth < 860 ? 34 : 30, 38) ;
    const tick = (now) => {
      if (!settled) {
        const k = Math.min(1, (now - t0) / dur);
        angle = from + (target - from) * ease(k);
        if (k >= 1) {
          settled = true;
          const i = FINISHES.indexOf(current);
          focusIdx = i;
          items[i].el.classList.add('focus');
          powerUp(items[i].bars);
        }
      }
      items.forEach(({ el }, i) => {
        const a = ((i * 72 - angle) * Math.PI) / 180;
        const z = Math.cos(a), x = Math.sin(a), f = (z + 1) / 2;
        el.style.transform = `translate(-50%,-50%) translateX(${x * R()}vmin) scale(${0.32 + 0.68 * f ** 2.4})`;
        el.style.zIndex = Math.round(z * 100) + 100;
        el.style.opacity = 0.18 + 0.82 * f ** 1.6;
        el.style.filter = `brightness(${0.4 + 0.6 * f})`;
      });
      hero._raf = requestAnimationFrame(tick);
    };
    const powerUp = (bs) => {
      if (reduce.matches) { setLights(bs, { all: 1 }); return; }
      let n = 0;
      setLights(bs, { fill: 0 });
      clearInterval(hero._pow);
      hero._pow = setInterval(() => { n += 1; setLights(bs, { fill: n }); if (n >= 4) clearInterval(hero._pow); }, 240);
    };
    const showOrbit = (f) => {
      const i = FINISHES.indexOf(f);
      if (focusIdx >= 0) { items[focusIdx].el.classList.remove('focus'); clearInterval(hero._pow); setLights(items[focusIdx].bars, { all: 0 }); }
      let t = i * 72;
      while (t - angle > 180) t -= 360;
      while (angle - t > 180) t += 360;
      from = angle; target = t; t0 = performance.now(); settled = false;
      if (reduce.matches) { angle = target; dur = 1; }
    };

    const apply = () => {
      cancelAnimationFrame(hero._raf);
      if (hero.dataset.motion === 'orbit') { orbit.hidden = false; shot.parentElement.hidden = true; angle = from = target = FINISHES.indexOf(current) * 72 - 72; showOrbit(current); hero._raf = requestAnimationFrame(tick); }
      else { orbit.hidden = true; shot.parentElement.hidden = false; showFloat(current); }
    };
    subs.push((f) => (hero.dataset.motion === 'orbit' ? showOrbit(f) : showFloat(f)));

    // auto-cycle every 3 s until someone picks a finish themselves
    let timer = null;
    const start = () => {
      if (reduce.matches) return;
      stop();
      sw && sw.classList.add('cycling');
      timer = setInterval(() => {
        const next = FINISHES[(FINISHES.indexOf(current) + 1) % FINISHES.length];
        setFinish(next.id, 'cycle');
        // restart the ring animation on the new swatch
        sw && (sw.classList.remove('cycling'), void sw.offsetWidth, sw.classList.add('cycling'));
      }, CYCLE);
    };
    const stop = () => { clearInterval(timer); timer = null; sw && sw.classList.remove('cycling'); };
    subs.push((f, why) => { if (why === 'user') stop(); });
    subs.push((f, why) => { if (why === 'user') hero._picked = true; });
    // only cycle while the hero is on screen and the tab is visible
    let inView = true;
    const maybe = () => (inView && !document.hidden && !hero._picked ? !timer && start() : stop());
    new IntersectionObserver(([e]) => { inView = e.isIntersecting; maybe(); }, { threshold: 0.35 }).observe(hero);
    document.addEventListener('visibilitychange', maybe);

    hero._apply = apply;
    apply();
    maybe();
  };

  /* ---------- variation panel (mockup only) ---------- */
  const wireVary = () => {
    const panel = $('[data-vary]');
    const hero = $('[data-hero]');
    if (!panel || !hero) return;
    const tgt = (b) => (b.dataset.target ? $(b.dataset.target) : hero);
    const sync = () => $$('[data-v]', panel).forEach((b) => b.setAttribute('aria-pressed', String(tgt(b).dataset[b.dataset.v] === b.dataset.val)));
    $$('[data-v]', panel).forEach((b) => b.addEventListener('click', () => {
      tgt(b).dataset[b.dataset.v] = b.dataset.val;
      window.dispatchEvent(new Event('scroll'));
      $$('[data-glow-host]').forEach((h) => (h.dataset.glow = hero.dataset.glow));
      const u = new URL(location.href);
      u.searchParams.set(b.dataset.v, b.dataset.val);
      history.replaceState(null, '', u);
      if (b.dataset.v === 'motion') hero._apply();
      sync();
    }));
    $('.x', panel).addEventListener('click', () => panel.remove());
    $('strong', panel).addEventListener('click', () => panel.classList.toggle('open'));
    sync();
  };

  /* ---------- sticky buy bar ---------- */
  const wireBuybar = () => {
    const bar = $('[data-buybar]');
    const after = $('[data-buybar-after]');
    if (!bar || !after) return;
    const io = new IntersectionObserver(([e]) => {
      const show = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.classList.toggle('show', show);
      document.body.classList.toggle('has-buybar', show);
    });
    io.observe(after);
  };

  /* ---------- how it works ---------- */
  const STEPS = [
    { layer: 'closed', cam: 'full', stick: () => null, lights: (t) => ({ all: Math.min(1, t * 2.4) * (t < 0.8 ? 1 : Math.max(0, 1 - (t - 0.8) / 0.15)) }) },
    { layer: 'open', cam: 'full', stick: () => null, lights: () => ({ all: 0 }) },
    { layer: 'open', cam: 'top', stick: (t) => Math.min(1, Math.max(0, (t - 0.08) / 0.62)), lights: (t) => (t > 0.7 ? { level: 0, pulseBottom: true } : { all: 0 }), buzzAt: 0.7 },
    { layer: 'open', cam: 'slot', stick: () => 1, lights: (t) => ({ fill: t * 4.25 }), buzzAt: 0.94 },
    { layer: 'open', cam: 'slot', stick: () => 1, lights: (t) => ({ level: 4 - Math.floor(t * 3.999) }) },
    { layer: 'open', cam: 'slot', stick: () => 1, lights: () => ({ level: 1, pulseBottom: true }), buzzAt: 0.05 },
    { layer: 'open', cam: 'top', stick: (t) => (t < 0.08 ? 1 : Math.max(0, 1 - (t - 0.08) / 0.5)), lights: () => ({ all: 0 }), toClosedAt: 0.82 },
  ];
  const CAM = { full: 'scale(1)', top: 'translate(8%, 18%) scale(1.35)', slot: 'translate(16%, -2%) scale(1.85)' };

  const wireHowTo = () => {
    const root = $('[data-howto]');
    if (!root) return;
    const stage = $('[data-howto-stage]', root);
    const steps = $$('[data-step]', root);
    const closed = $('[data-layer=closed]', root);
    const open = $('[data-layer=open]', root);
    const frame = $('[data-frame]', open);
    const barsUp = $$('[data-layer=closed] .light', root);
    const barsAng = $$('[data-layer=open] .light', root);
    const well = $('[data-well]', root);
    const stick = $('[data-stick]', root);
    const ticks = $$('[data-ticks] i', root);

    placeAngled(barsAng);
    const placeAll = (f) => {
      placeUpright(barsUp, f);
      $('img', closed).src = upright(f, true);
      $('img', frame).src = `${A}${f.id}-open-hd.webp`;
    };
    subs.push(placeAll);
    placeAll(current);

    // the stick sits in a clipped well whose bottom edge is the stick opening
    const [hx, hy] = ANGLED.hole;
    Object.assign(well.style, { left: (hx - ANGLED.stick / 2) * 100 + '%', width: ANGLED.stick * 100 + '%', bottom: (1 - hy) * 100 + '%', transform: `rotate(${-ANGLED.tilt}deg)` });
    const STICK_LEN = 6.16, SHOWN = 1.32, LIFT = 1.6; // in stick diameters
    const setStick = (p) => {
      if (p === null) { stick.style.opacity = 0; return; }
      const d = well.clientWidth;
      const bottom = p >= 0 ? (-(STICK_LEN - SHOWN) * p + LIFT * (1 - p)) * d : LIFT * d;
      stick.style.opacity = 1;
      stick.style.transform = `translateY(${-bottom}px)`;
    };

    // film mode: scroll scrubs the frame sequence instead of the render stand-in
    const canvas = $('[data-film-canvas]', root);
    const B = (root.dataset.filmSteps || '').split(',').map(Number);
    const phone = matchMedia('(max-width: 749px)').matches;
    const total = B[B.length - 1] || 0;
    const frames = [];
    let loaded = false;
    const loadFilm = () => {
      if (loaded || !canvas) return;
      loaded = true;
      const base = root.dataset.film + (phone ? 'm-' : 'd-');
      const count = phone ? Math.ceil(total / 2) : total;
      for (let i = 1; i <= count; i++) { const im = new Image(); if (i % 24 === 1) im.onload = () => root.dataset.mode === 'film' && requestAnimationFrame(update); im.src = `${base}f${String(i).padStart(4, '0')}.webp`; frames[i] = im; }
    };
    const drawFilm = (idx, t) => {
      if (!canvas) return;
      loadFilm();
      let f = Math.round(B[idx] + (B[idx + 1] - B[idx]) * t);
      if (phone) f = Math.ceil(f / 2);
      let im = frames[f];
      for (let k = f; (!im || !im.complete || !im.naturalWidth) && k > 1; k--) im = frames[k - 1];
      if (!im || !im.naturalWidth) return;
      const dpr = Math.min(2, devicePixelRatio || 1), w = canvas.clientWidth * dpr, h = canvas.clientHeight * dpr;
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      const ctx = canvas.getContext('2d');
      // transparent frames over the dark stage; the stage has the film's 9:16 shape so hands meet its edge
      const s = Math.max(w / im.naturalWidth, h / im.naturalHeight);
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(im, (w - im.naturalWidth * s) / 2, h - im.naturalHeight * s, im.naturalWidth * s, im.naturalHeight * s);
    };
    const mode = new URLSearchParams(location.search).get('howto');
    if (mode) root.dataset.mode = mode;

    let last = -1, buzzed = {};
    const buzz = () => {
      if (reduce.matches) return;
      stage.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-2px)' }, { transform: 'translateX(2px)' }, { transform: 'translateX(-1px)' }, { transform: 'translateX(0)' }], { duration: 240, iterations: 2 });
    };
    const update = () => {
      const r = root.getBoundingClientRect();
      const p = Math.min(0.9999, Math.max(0, -r.top / (r.height - innerHeight)));
      const idx = Math.floor(p * STEPS.length);
      const t = p * STEPS.length - idx;
      const s = STEPS[idx];
      if (idx !== last) {
        steps.forEach((el, i) => { el.classList.toggle('is-active', i === idx); el.classList.toggle('is-done', i < idx); });
        ticks.forEach((el, i) => el.classList.toggle('on', i <= idx));
        frame.style.transform = CAM[s.cam];
        root.dataset.step = idx;
        last = idx;
      }
      if (root.dataset.mode === 'film') { drawFilm(idx, t); return; }
      const layer = s.toClosedAt && t > s.toClosedAt ? 'closed' : s.layer;
      closed.classList.toggle('is-shown', layer === 'closed');
      open.classList.toggle('is-shown', layer === 'open');
      setStick(s.stick(t));
      const L = s.lights(t);
      setLights(barsUp, L);
      setLights(barsAng, L);
      if (s.buzzAt !== undefined) {
        const k = idx + ':' + s.buzzAt;
        if (t >= s.buzzAt && !buzzed[k]) { buzzed[k] = 1; buzz(); }
        if (t < s.buzzAt - 0.05) buzzed[k] = 0;
      }
    };
    addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    addEventListener('resize', update);
    update();
  };

  /* ---------- product gallery (device page) ---------- */
  const wireGallery = () => {
    const g = $('[data-gallery]');
    if (!g) return;
    const main = $('[data-gallery-main]', g);
    const thumbs = $('[data-gallery-thumbs]', g);
    let view = 0;
    const draw = (f) => {
      thumbs.innerHTML = f.views.map((v, i) => `<button type="button" aria-label="View ${i + 1}" aria-pressed="${i === view}" data-view="${i}"><img src="${img(f, v)}" alt=""></button>`).join('');
      main.src = i0(f);
      $$('[data-view]', thumbs).forEach((b) => b.addEventListener('click', () => { view = +b.dataset.view; draw(current); }));
    };
    const i0 = (f) => (view === 0 ? upright(f, true) : img(f, f.views[view]));
    subs.push(draw);
    draw(current);
  };

  const wireQty = () => $$('[data-qty-wrap]').forEach((w) => {
    const input = $('[data-qty]', w);
    $$('[data-qd]', w).forEach((b) => b.addEventListener('click', () => { input.value = Math.max(1, Math.min(10, (+input.value || 1) + +b.dataset.qd)); }));
  });

  document.addEventListener('DOMContentLoaded', () => {
    injectChrome();
    buildSwatches();
    buildFlavours();
    const p = new URLSearchParams(location.search);
    setFinish(p.get('finish') || FINISHES[0].id, 'init');
    wireHero();
    wireBuybar();
    wireHowTo();
    wireVary();
    wireGallery();
    wireQty();
    Bag.render();
    Bag.sync();
  });

  window.IQ = { FINISHES, FLAVOURS, setFinish, Bag, stickItem, deviceItem, inr, on: (fn) => subs.push(fn), get current() { return current; } };
})();
